import { RefObject, useEffect, useMemo } from 'react';
import { Canvas, FabricObject, IText } from 'fabric';
import type { FieldBinding } from '@doer/frontend-components/creativity-editor';
import type { RoutedFormData } from '../../../types/templates';
import { buildPieceIdToBindingMap } from '../binding';
import { computeNextFieldValue } from '../binding/binders';

type ApplyToForm = (fieldKey: string, value: unknown) => void;

/**
 * Pipes in-canvas text edits back to formData on `text:editing:exited`.
 * `isApplyingRef` guards against the binder's own writes bouncing back as user edits.
 */
export const useCanvasToFormSync = (
  canvas: Canvas | null,
  fieldBindings: FieldBinding[],
  formDataRef: RefObject<RoutedFormData>,
  isApplyingRef: RefObject<boolean>,
  applyToForm: ApplyToForm
): void => {
  const pieceMap = useMemo(() => buildPieceIdToBindingMap(fieldBindings), [fieldBindings]);

  useEffect(() => {
    if (!canvas) return;

    const handleExit = (e: { target?: FabricObject }) => {
      if (isApplyingRef.current) return;

      const obj = e.target;
      // IText covers pills and Textbox (Textbox extends IText).
      if (!obj?.pieceId || !(obj instanceof IText)) return;

      if (obj.linkedAcrossResolutions === false) return;

      const entry = pieceMap.get(obj.pieceId);
      if (!entry || entry.bindingType !== 'text') return;

      const newValue = obj.text ?? '';
      applyToForm(entry.fieldKey, computeNextFieldValue(entry, formDataRef.current, newValue));
    };

    canvas.on('text:editing:exited', handleExit);
    return () => {
      canvas.off('text:editing:exited', handleExit);
    };
  }, [canvas, pieceMap, applyToForm, formDataRef, isApplyingRef]);
};
